import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { DrawerProps } from '../../components/Drawer';
import { envConfig } from '../../utils/envConfig';
import { isCypressWindow } from '../../utils/envCypress';
import { AppwideDrawerContentType } from './AppwideDrawerContentType';

interface AppwideDrawerNavigationBehaviorProps {
  drawer: DrawerProps;
  contentSpec: { type: AppwideDrawerContentType; persistOnNavigate?: boolean } | undefined;
}

interface NavigationConditions {
  allowedRoutes: readonly string[];
}

const NavigationConditionsMap: Partial<Record<AppwideDrawerContentType, NavigationConditions>> = {
  [AppwideDrawerContentType.InstrumentInspector]: {
    allowedRoutes: ['/trading', '/transfers'] as const,
  },
};

function AppwideDrawerNavigationBehaviorInner({ drawer, contentSpec }: AppwideDrawerNavigationBehaviorProps) {
  const location = useLocation();
  const [previousPath, setPreviousPath] = useState(location.pathname);

  useEffect(() => {
    if (!drawer.isOpen || !contentSpec?.type) {
      setPreviousPath(location.pathname);
      return;
    }

    if (contentSpec.persistOnNavigate) {
      setPreviousPath(location.pathname);
      return;
    }

    if (contentSpec.type === AppwideDrawerContentType.EntityAdmin && previousPath !== location.pathname) {
      drawer.close();
      setPreviousPath(location.pathname);
      return;
    }

    const navigationConditions = NavigationConditionsMap[contentSpec.type];
    if (navigationConditions) {
      const { allowedRoutes } = navigationConditions;
      const wasOnAllowedRoute = allowedRoutes.some((route: string) => previousPath.startsWith(route));
      const isOnAllowedRoute = allowedRoutes.some((route: string) => location.pathname.startsWith(route));

      if (wasOnAllowedRoute && !isOnAllowedRoute) {
        drawer.close();
      }
    }

    setPreviousPath(location.pathname);
  }, [location.pathname, previousPath, contentSpec, drawer]);

  return null;
}

/**
 * Prevents the navigation behavior unless the app-wide drawer is in a
 * state that matches the NavigationConditionsMap type.
 *
 * @param drawer - The drawer to monitor
 * @param contentSpec - The content spec of the drawer
 */
export function AppwideDrawerNavigationBehavior({ drawer, contentSpec }: AppwideDrawerNavigationBehaviorProps) {
  // Don't run in vitest environment to avoid useLocation hook needs; cypress is ok as it's in a router
  if (envConfig.VITE_AVA_ENV === 'test' && !isCypressWindow(window)) {
    return null;
  }

  // If no content spec, don't run
  if (!contentSpec?.type) {
    return null;
  }

  // Call the internal function directly since we're in the same file
  return <AppwideDrawerNavigationBehaviorInner drawer={drawer} contentSpec={contentSpec} />;
}
